import { SimulatorTrainingType } from 'modules/simulatorTrainings/common/types'

// Card
export enum CardType {
  ElearningCategory,
  RequirementsGroup,
  UserRequirement,
  TrainingModule, // Elearning
  PocketGuide, // Subtype of elearning - Quick guide
  TrainingVideo, // Subtype of elearning - Video
  Rapid,
  DistanceCourse,
  Survey,
  Certificate,
  CompanyNotice,
  Course,
  Cst,
  Education,
  SelfAssessment,
  OnboardAssessment,
  ServiceRecord,
  CesStcw,
  CesDetailed,
  CesCompanySpecific,
  Ppro,
  Apro,
  Program,
  VirtualEventsCategory,
  VirtualEventsEvent,
  VirtualEventSchedule,
  SimulatorTraining,
  MarlinsEnglishTest,
  Unknown,
}

export enum ElearningStatuses {
  NotStarted,
  InProgress,
  Completed,
}

export type ApiCardsDataResponseElearningSpecificValues = {
  productNumber: string
  numberOfChapters: number
  estimatedDuration: string
  imageTimeStamp: number
  deadline: Nullable<DateTime>
  targetGroups?: string[]
}

export type ApiCardsDataResponseSurveySpecificValues = {
  categoryId: Nullable<Guid>
  productNumber: string
  deadline: Nullable<DateTime>
}

export type ApiCardsDataResponseRapidElearningSpecificValues = {
  categoryId: Nullable<Guid>
  productNumber: string
  numberOfChapters: number
  deadline: Nullable<DateTime>
}

export type ApiCardsDataResponseDistanseCourseSpecificValues = {
  productNumber: string
  numberOfParts: number
  deadline: Nullable<DateTime>
}

export type ApiCardsDataResponseCertificateSpecificValues = {
  countryName: string
  flagId: Guid
  flagName: string
  issuingCountryId: Guid
}

export type ApiCardsDataResponseServiceRecordSpecificValues = {
  rank: string
  duration: string
  vesselType: string
  competenceId: Guid
  engineTypeId: string | null
  engineTypeName?: string | null
  minimumEnginePower: number
  minimumGrossTonnage: number
  minimumMonths: number
  rankId: Nullable<Guid>
  rankName?: string
  vesselTypeId: string
  vesselTypeName: string
}

export type ApiCardsDataResponseCesDetailedSpecificValues = {
  activityId: Guid
  functionArea: string
  detailedSubject: string
  deadline: Nullable<DateTime>
  minimumScore: number
  cesScheduleId: Nullable<Guid>
}

export type ApiCardsDataResponseCesStcwSpecificValues = {
  activityId: Guid
  department: string
  level: string
  vesselType: string
  propulsion: string
  deadline: Nullable<DateTime>
  minimumScore: number
  cesScheduleId: Nullable<Guid>
}

export type ApiCardsDataResponseProgramDetailsValues = {
  programId: string
}

export type ApiCardsDataResponseCesCstSpecificValues = {
  activityId: Guid
  testName: string
  deadline: Nullable<DateTime>
  minimumScore: number
  cesScheduleId: Nullable<Guid>
}

export enum CompetenceStatuses {
  Approved,
  Disapproved,
  Outdated,
  NotYetApproved,
}

export type ApiCardsDataResponseUserRequirementSpecificValues = {
  parentId: Guid
  competenceStatus: CompetenceStatuses
  isInProgress: boolean
  isLocked: boolean
  isReadyToBeAssessed: boolean
  isCriticalForRank: boolean
  shortName?: string
  validity?: string
  requirementId?: Guid
  // TODO: Fix type with real API
  // Dictionary array
  // It's mandatory for filtering when you select activity type
  trainingTypeList?: any[]
}

export type ApiCardsDataResponseAssessmentSpecificValues = {
  competenceId: Guid
  deadline: Nullable<DateTime>
}

export type ApiCardsDataResponseElearningCategorySpecificValues = {
  numberOfModules: number
}

export type ApiCardsDataResponseRequirementsGroupSpecificValues = {
  parentId?: Nullable<Guid>
  totalRequirements: number
  approvedRequirements: number
  hasPhoto: boolean
  photoTimestamp?: number | null
}

export type ApiCardsDataResponseCompanyNoticeSpecificValues = {
  activityId: Guid
  deadline: Nullable<DateTime>
}

export type ApiCardsDataResponseVirtualEventsCategorySpecificValues = {
  numberOfEvents: number
}

export type ApiCardsDataResponseVirtualEventsEventSpecificValues = {
  numberOfSchedules: number
  categoryId: Nullable<Guid>
}

export enum VirtualEventRole {
  Participant,
  Presenter,
}

export type ApiCardsDataResponseVirtualEventsMyEventSpecificValues = {
  role: VirtualEventRole
  date: DateTime
  timeZone: TimeZone
  duration: string
  isPastEvent: boolean
}

export type ApiCardsDataResponseSimulatorTrainingSpecificValues = {
  deadline: DateTime
  simulatorTrainingType: SimulatorTrainingType
  trainingId: Guid
  type: number | null
}

export type ApiCardsDataResponseMarlinsEnglishTestSpecificValues = {
  deadline: DateTime
  scheduleId: Guid
}

export type ApiCardsDataResponseSpecificValues =
  | ApiCardsDataResponseElearningSpecificValues
  | ApiCardsDataResponseSurveySpecificValues
  | ApiCardsDataResponseDistanseCourseSpecificValues
  | ApiCardsDataResponseRapidElearningSpecificValues
  | ApiCardsDataResponseCertificateSpecificValues
  | ApiCardsDataResponseServiceRecordSpecificValues
  | ApiCardsDataResponseCesDetailedSpecificValues
  | ApiCardsDataResponseCesStcwSpecificValues
  | ApiCardsDataResponseUserRequirementSpecificValues
  | ApiCardsDataResponseElearningCategorySpecificValues
  | ApiCardsDataResponseRequirementsGroupSpecificValues
  | ApiCardsDataResponseCompanyNoticeSpecificValues
  | ApiCardsDataResponseVirtualEventsCategorySpecificValues
  | ApiCardsDataResponseVirtualEventsEventSpecificValues
  | ApiCardsDataResponseVirtualEventsMyEventSpecificValues
  | ApiCardsDataResponseSimulatorTrainingSpecificValues
  | ApiCardsDataResponseMarlinsEnglishTestSpecificValues

export type ApiCardsDataResponseActivityResult = {
  id: Guid
  isCompleted: boolean
}

export type Card = {
  id: Guid
  name: string
  itemType: CardType
  typeSpecificValues?: ApiCardsDataResponseSpecificValues | {}
  result?: Nullable<ApiCardsDataResponseActivityResult>
}

export type ApiCardsDataResponse = {
  search: Card[]
}
